import {
  asMemberId,
  asObservationId,
  asOrderId,
  asOrderSetId,
  asProcedureId,
  type MemberId,
  type ObservationId,
  type OrderId,
  type OrderSetId,
  type ProcedureId,
  type ProcedureObservationComponent,
} from "@repo/domain";
import { z } from "zod";
import { unitSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";

const domainIdSchema = z.string().trim().min(1);

const orderSetIdSchema: z.ZodType<OrderSetId, z.ZodTypeDef, string> = z.string().trim().min(1).transform(asOrderSetId);

export const treatmentProcedureIdSchema: z.ZodType<ProcedureId, z.ZodTypeDef, string> = z
  .string()
  .trim()
  .min(1)
  .transform(asProcedureId);

const memberIdSchema: z.ZodType<MemberId, z.ZodTypeDef, string> = z.string().trim().min(1).transform(asMemberId);

export const treatmentAnsweredValueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Quantity"), value: z.number(), unit: unitSchema.optional() }),
  z.object({ kind: z.literal("Integer"), value: z.number().int() }),
  z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
  z.object({
    kind: z.literal("Choice"),
    selected: z.array(memberIdSchema),
    freeText: z.string().optional(),
  }),
  z.object({ kind: z.literal("Text"), value: z.string() }),
]);

const treatmentObservationFactsSchema = z.object({
  observationId: domainIdSchema.transform(asObservationId),
  trialId: domainIdSchema.transform(asObservationId).optional(),
  definitionId: memberIdSchema,
  observedAt: z.string().trim().min(1),
  components: z.array(z.object({ componentId: memberIdSchema, value: treatmentAnsweredValueSchema })).min(1),
  note: z.string().optional(),
});

export interface TreatmentObservationFacts {
  trialId?: ObservationId;
  observationId: ObservationId;
  definitionId: MemberId;
  observedAt: string;
  components: ProcedureObservationComponent[];
  note?: string;
}

export interface TreatmentObservationInputFacts {
  trialId?: string;
  observationId: string;
  definitionId: string;
  observedAt: string;
  components: Array<{
    componentId: string;
    value:
      | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
      | { kind: "Integer"; value: number }
      | { kind: "Boolean"; value: boolean }
      | { kind: "Choice"; selected: string[]; freeText?: string }
      | { kind: "Text"; value: string };
  }>;
  note?: string;
}

export const treatmentProcedureIdentitySchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});

export const completeTreatmentProcedureInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type CompleteTreatmentProcedureInput = z.infer<typeof completeTreatmentProcedureInputSchema>;

export const markTreatmentProcedureUnableToCompleteInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type MarkTreatmentProcedureUnableToCompleteInput = z.infer<
  typeof markTreatmentProcedureUnableToCompleteInputSchema
>;

export const markTreatmentProcedureDidNotPerformInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type MarkTreatmentProcedureDidNotPerformInput = z.infer<typeof markTreatmentProcedureDidNotPerformInputSchema>;

/** Undo `markUnableToComplete`: the work is under way again and the order is outstanding again. */
export const reopenTreatmentProcedureInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type ReopenTreatmentProcedureInput = z.infer<typeof reopenTreatmentProcedureInputSchema>;

/** Undo `markDidNotPerform`: the record is struck and the order goes back to outstanding. */
export const retractTreatmentProcedureDidNotPerformInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type RetractTreatmentProcedureDidNotPerformInput = z.infer<
  typeof retractTreatmentProcedureDidNotPerformInputSchema
>;

export const recordTreatmentProcedureObservationInputSchema: z.ZodType<
  TreatmentObservationFacts & { procedureId: string; orderSetId: OrderSetId; orderId: OrderId },
  z.ZodTypeDef,
  TreatmentObservationInputFacts & { procedureId: string; orderSetId: string; orderId: string }
> = treatmentObservationFactsSchema.extend({
  observationId: newEntityIdSchema.transform(asObservationId),
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
});
export type RecordTreatmentProcedureObservationInput = z.infer<typeof recordTreatmentProcedureObservationInputSchema>;

export const correctTreatmentProcedureObservationInputSchema: typeof recordTreatmentProcedureObservationInputSchema =
  recordTreatmentProcedureObservationInputSchema;
export type CorrectTreatmentProcedureObservationInput = z.infer<typeof correctTreatmentProcedureObservationInputSchema>;

export const retractTreatmentProcedureObservationInputSchema = z.object({
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  observationId: domainIdSchema.transform(asObservationId),
});
export type RetractTreatmentProcedureObservationInput = z.infer<typeof retractTreatmentProcedureObservationInputSchema>;

export const markTreatmentProcedureObservationUnableToCaptureInputSchema: z.ZodType<
  { procedureId: string; orderSetId: OrderSetId; orderId: OrderId; definitionId: MemberId },
  z.ZodTypeDef,
  { procedureId: string; orderSetId: string; orderId: string; definitionId: string }
> = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  definitionId: memberIdSchema,
});
export type MarkTreatmentProcedureObservationUnableToCaptureInput = z.infer<
  typeof markTreatmentProcedureObservationUnableToCaptureInputSchema
>;

export const clearTreatmentProcedureObservationUnableToCaptureInputSchema =
  markTreatmentProcedureObservationUnableToCaptureInputSchema;
export type ClearTreatmentProcedureObservationUnableToCaptureInput = z.infer<
  typeof clearTreatmentProcedureObservationUnableToCaptureInputSchema
>;

export const setTreatmentProcedureNoteInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  note: z.string().optional(),
});
export type SetTreatmentProcedureNoteInput = z.infer<typeof setTreatmentProcedureNoteInputSchema>;

export const adjustTreatmentProcedureTimesInputSchema = treatmentProcedureIdentitySchema
  .omit({ procedureId: true })
  .extend({
    start: z.string().datetime({ offset: true }).optional(),
    end: z.string().datetime({ offset: true }).optional(),
  });
export type AdjustTreatmentProcedureTimesInput = z.infer<typeof adjustTreatmentProcedureTimesInputSchema>;
